import { ProductGroupFilter } from '../products/productGroupFilter';
import { NotificationManager } from 'react-notifications';
import { apiFetch } from '@ami/react/core';

const _baseEndPoint = 'product-groups';

export const ProductGroupTypes = Object.freeze({
  Coupler: { id: 'Coupler', label: 'Coupler' },
  Part: { id: 'Part', label: 'Part' },
  StandardDiggingBucket: {
    id: 'StandardDiggingBucket',
    label: 'Standard Digging Bucket',
  },
  SpadeToothDiggingBucket: {
    id: 'SpadeToothDiggingBucket',
    label: 'Spade Tooth Digging Bucket',
  },
  RecessedDiggingBucket: {
    id: 'RecessedDiggingBucket',
    label: 'Recessed Digging Bucket',
  },
  SpadeToothRecessedDiggingBucket: {
    id: 'SpadeToothRecessedDiggingBucket',
    label: 'Spade Tooth Recessed Digging Bucket',
  },
  DitchingBucket: { id: 'DitchingBucket', label: 'Ditching Bucket' },
  TiltDitchingBucket: {
    id: 'TiltDitchingBucket',
    label: 'Tilt Ditching Bucket',
  },
  Attachment: { id: 'Attachment', label: 'Attachment' },
  StickPivotThumb: { id: 'StickPivotThumb', label: 'Stick Pivot Thumb' },
  ExtendableStickThumb: { id: 'ExtendableStickThumb', label: 'Extendable Stick Thumb' },
  ProLinkThumb: { id: 'ProLinkThumb', label: 'Pro Link Thumb' },
  WeldOnThumb: { id: 'WeldOnThumb', label: 'Weld On Thumb' },
});

export class ProductGroupDimension {
  constructor(json) {
    if (json) {
      this.dimensionId = json.dimensionId;
      this.name = json.name;
      this.type = json.type;
      this.categoryType = json.categoryType;
      this.visibilityType = json.visibilityType;
      this.isSystem = json.isSystem;
      return;
    }
    this.dimensionId = null;
    this.name = '';
    this.type = null;
    this.categoryType = null;
    this.visibilityType = null;
    this.isSystem = false;
  }
}

export const ProductGroupDimensionVisibilityType = Object.freeze({
  VisibleOnlineAndOnPdf: 'VisibleOnlineAndOnPdf',
  VisibleOnlineOnly: 'VisibleOnlineOnly',
  NotVisible: 'NotVisible',
});

export class ProductGroupRestrictedRegion {
  constructor(json) {
    this.countryCode = json.countryCode;
    this.regionCode = json.regionCode;
  }
}

export const ProductGroupVisibility = Object.freeze({
  VisiblePublic: 'Visible to AMI and Dealers',
  VisibleInternalRestrictedPublic: 'Visible to AMI, Restricted to Dealers',
  VisibleInternal: 'Visible to AMI',
  Hidden: 'Hidden',
});

export class ProductMarketing {
  constructor(json) {
    if (json) {
      this.supportsHardox = json.supportsHardox;
      this.supportsStrenx = json.supportsStrenx;
      return;
    }
    this.supportsHardox = false;
    this.supportsStrenx = false;
  }
}

export class ProductGroupNotesField {
  constructor(json) {
    if (json) {
      this.name = json.name || '';
      this.description = json.description || '';
      this.isRequired = !!json.isRequired;
      return;
    }
    this.name = '';
    this.description = '';
    this.isRequired = false;
  }
  clone() {
    return new ProductGroupNotesField({ ...this });
  }
}

export class CopyProductGroupData {
  constructor(json) {
    if (json) {
      Object.keys(json).forEach((key) => (this[key] = json[key]));
      return;
    }
    this.productGroupId = '';
    this.name = '';
  }
}

export class ProductGroup {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.productCategoryIds = item.productCategoryIds;
      this.productCategories = item.productCategories;
      this.name = item.name;
      this.installationOptionId = item.installationOptionId;
      this.shippingCategoryId = item.shippingCategoryId;
      this.description = item.description;
      this.type = item.type;
      this.restrictedCouplerDecisions = item.restrictedCouplerDecisions;
      this.imageKey = item.imageKey;
      this.imageUrl = item.imageUrl;
      this.sortOrder = item.sortOrder;
      this.dimensions = item.dimensions.map(
        (x) => new ProductGroupDimension(x)
      );
      this.restrictedRegions = item.restrictedRegions.map(
        (x) => new ProductGroupRestrictedRegion(x)
      );
      this.productOptionSetIds = item.productOptionSetIds;
      this.carrierId = item.carrierId;
      this.carrierName = item.carrierName;
      this.attachmentPointId = item.attachmentPointId;
      this.attachmentPointName = item.attachmentPointName;
      this.marketing = new ProductMarketing(item.marketing);
      this.marketingSlug = item.marketingSlug;
      this.notesField = item.notesField
        ? new ProductGroupNotesField(item.notesField)
        : null;
      this.serialNumbersEnabled = item.serialNumbersEnabled;
      this.serialNumbersAutoGenerate = item.serialNumbersAutoGenerate;
      this.partialEngineeringDesignsSupported = item.partialEngineeringDesignsSupported;
      this.partialEngineeringDesignsEnabledForAllWeightCategories = item.partialEngineeringDesignsEnabledForAllWeightCategories;
      this.partialEngineeringDesignWeightCategoryIds = item.partialEngineeringDesignWeightCategoryIds;    
      this.partialEngineeringDesignWeightCategories = item.partialEngineeringDesignWeightCategories;
      this.requiresCoupler = item.requiresCoupler;
      this.hasHydraulics = item.hasHydraulics;
      this.partNumberLogic = item.partNumberLogic;
      this.visibility = item.visibility;
      return;
    }
    this.id = null;
    this.productCategoryIds = [];
    this.productCategories = [];
    this.name = '';
    this.installationOptionId = null;
    this.shippingCategoryId = null;
    this.description = '';
    this.type = 'Coupler';
    this.restrictedCouplerDecisions = [];
    this.imageKey = null;
    this.imageUrl = null;
    this.sortOrder = '';
    this.dimensions = [];
    this.restrictedRegions = [];
    this.productOptionSetIds = [];
    this.carrierId = null;
    this.carrierName = '';
    this.attachmentPointId = null;
    this.attachmentPointName = '';
    this.marketing = new ProductMarketing();
    this.marketingSlug = '';
    this.notesField = null;
    this.serialNumbersEnabled = false;
    this.serialNumbersAutoGenerate = false;
    this.partialEngineeringDesignsSupported = false;
    this.partialEngineeringDesignsEnabledForAllWeightCategories = false;
    this.partialEngineeringDesignWeightCategoryIds = [];
    this.partialEngineeringDesignWeightCategories = [];
    this.partNumberLogic = 'Standalone';
    this.visibility = 'VisiblePublic';
    this.requiresCoupler = false;
    this.hasHydraulics = false;
  }

  static search(filter = new ProductGroupFilter(), page, isPublic) {
    filter.setPaginationParameters(page);
    return apiFetch(
      `${_baseEndPoint}?${filter.toQueryString()}`,
      'GET',
      null,
      null,
      isPublic
    );
  }

  static async getById(id) {
    const entity = await apiFetch(`${_baseEndPoint}/${id}`);
    return new ProductGroup(entity);
  }

  static async getForExportById(id) {
    return await apiFetch(`${_baseEndPoint}/${id}/export/csv`);
  }

  static async synchronize(id) {
    return await apiFetch(`${_baseEndPoint}/${id}/synchronize`, 'POST');
  }

  static create(item) {
    return apiFetch(_baseEndPoint, 'POST', item);
  }

  update() {
    return apiFetch(`${_baseEndPoint}/${this.id}`, 'PUT', this);
  }

  delete() {
    return apiFetch(`${_baseEndPoint}/${this.id}`, 'DELETE', this);
  }

  static copy(copyData) {
    return apiFetch(
      `${_baseEndPoint}/copy`,
      'POST',
      { ...copyData },
      {
        automaticSuccessNotification: false,
      }
    ).then((result) => {
      NotificationManager.success('Product Group copied');
      return result.id;
    });
  }
}
